<template>
  <div class="controlled-reader">
    <div v-if="initializing" class="controlled-reader__state">
      <span class="controlled-reader__spinner"></span>
      <p>正在初始化阅读进度...</p>
    </div>

    <div v-else-if="fatalError" class="controlled-reader__state is-error">
      <p>{{ fatalError }}</p>
      <button type="button" class="reader-button is-primary" @click="initialize">重新加载</button>
    </div>

    <template v-else>
      <div class="controlled-reader__toolbar">
        <div class="controlled-reader__navigation">
          <button
            type="button"
            class="reader-button"
            :disabled="actionLoading || !resourceReady || !canTurnPrevious || remainingSeconds > 0"
            @click="turnTo(currentPage - 1)"
          >
            上一页
          </button>
          <span class="controlled-reader__page">
            {{ currentPage }} / {{ effectivePageEnd }}
          </span>
          <button
            type="button"
            class="reader-button"
            :disabled="actionLoading || !resourceReady || !canTurnNext || remainingSeconds > 0"
            @click="turnTo(currentPage + 1)"
          >
            下一页
          </button>
          <span v-if="!resourceReady" class="controlled-reader__wait">页面加载完成后开始计时</span>
          <span v-else-if="remainingSeconds > 0" class="controlled-reader__wait">
            {{ remainingSeconds }} 秒后可翻页
          </span>
          <span v-else-if="pageTurnLimit > 0" class="controlled-reader__ready">可以翻页</span>
        </div>

        <div class="controlled-reader__actions">
          <slot name="toolbar"></slot>
          <span v-if="isCompleted" class="controlled-reader__completed">已完成阅读</span>
          <button
            v-else-if="currentPage === effectivePageEnd"
            type="button"
            class="reader-button is-primary"
            :disabled="actionLoading || !resourceReady || remainingSeconds > 0"
            @click="completeReading"
          >
            {{ actionLoading ? '提交中...' : '完成阅读' }}
          </button>
        </div>
      </div>

      <div v-if="actionError" class="controlled-reader__message" role="alert">
        {{ actionError }}
      </div>

      <div class="controlled-reader__body">
        <slot
          :current-page="currentPage"
          :progress="progress"
          :action-loading="actionLoading"
        ></slot>
      </div>
    </template>
  </div>
</template>

<script>
import {
  completeTextbookProgress,
  openTextbookProgress,
  turnTextbookPage
} from '@/api/courseTextbook'

const parseServerTime = (value) => {
  if (!value) return 0
  const timestamp = new Date(String(value).replace(/-/g, '/')).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

export default {
  name: 'ControlledReaderShell',
  props: {
    textbookId: {
      type: [Number, String],
      required: true
    },
    resourceReady: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      initializing: true,
      actionLoading: false,
      fatalError: '',
      actionError: '',
      progress: {},
      remainingSeconds: 0,
      serverElapsedAtResponse: 0,
      responseReceivedAt: 0,
      countdownTimer: null
    }
  },
  computed: {
    currentPage() {
      return Number(this.progress.current_page) || Number(this.progress.effective_page_start) || 1
    },
    effectivePageStart() {
      return Number(this.progress.effective_page_start) || 1
    },
    effectivePageEnd() {
      return Number(this.progress.effective_page_end) || this.effectivePageStart
    },
    pageTurnLimit() {
      return Math.max(0, Number(this.progress.page_turn_limit) || 0)
    },
    canTurnPrevious() {
      return this.currentPage > this.effectivePageStart && Number(this.progress.can_turn_previous) !== 0
    },
    canTurnNext() {
      return this.currentPage < this.effectivePageEnd && Number(this.progress.can_turn_next) !== 0
    },
    isCompleted() {
      return Number(this.progress.is_completed) === 1
    }
  },
  watch: {
    textbookId() {
      this.initialize()
    },
    resourceReady(ready) {
      this.handleResourceState(ready)
    }
  },
  created() {
    this.initialize()
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    async initialize() {
      this.stopCountdown()
      this.initializing = true
      this.fatalError = ''
      this.actionError = ''

      try {
        const progress = await openTextbookProgress(this.textbookId)
        this.applyProgress(progress)
        this.$emit('ready', progress)
      } catch (error) {
        this.fatalError = error.message || '阅读进度初始化失败'
      } finally {
        this.initializing = false
      }
    },
    applyProgress(progress = {}) {
      this.progress = { ...progress }
      this.actionError = ''
      this.resetCountdownClock()
      this.$emit('progress-change', this.progress)
    },
    resetCountdownClock() {
      this.stopCountdown()
      if (!this.resourceReady) {
        this.serverElapsedAtResponse = 0
        this.responseReceivedAt = 0
        this.remainingSeconds = this.pageTurnLimit
        return
      }

      const serverNow = parseServerTime(this.progress.server_time)
      const enteredAt = parseServerTime(this.progress.page_entered_at)
      this.serverElapsedAtResponse = serverNow > 0 && enteredAt > 0
        ? Math.max(0, Math.floor((serverNow - enteredAt) / 1000))
        : 0
      this.responseReceivedAt = Date.now()
      this.refreshRemainingSeconds()
      this.startCountdown()
    },
    handleResourceState(ready) {
      this.stopCountdown()
      this.serverElapsedAtResponse = 0
      if (!ready) {
        this.responseReceivedAt = 0
        this.remainingSeconds = this.pageTurnLimit
        return
      }

      this.responseReceivedAt = Date.now()
      this.remainingSeconds = this.pageTurnLimit
      this.startCountdown()
    },
    refreshRemainingSeconds() {
      const localElapsed = this.responseReceivedAt > 0
        ? Math.max(0, Math.floor((Date.now() - this.responseReceivedAt) / 1000))
        : 0
      const elapsed = this.serverElapsedAtResponse + localElapsed
      this.remainingSeconds = Math.max(0, this.pageTurnLimit - elapsed)
    },
    startCountdown() {
      this.stopCountdown()
      if (this.remainingSeconds <= 0) return
      this.countdownTimer = window.setInterval(() => {
        this.refreshRemainingSeconds()
        if (this.remainingSeconds <= 0) {
          this.stopCountdown()
        }
      }, 1000)
    },
    stopCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    async requestPage(targetPage) {
      const page = Number(targetPage) || 0
      if (page === this.currentPage) return true
      if (page < this.effectivePageStart || page > this.effectivePageEnd) {
        this.actionError = `只能阅读第 ${this.effectivePageStart} 至 ${this.effectivePageEnd} 页`
        return false
      }
      if (Math.abs(page - this.currentPage) !== 1) {
        this.actionError = '受控阅读模式需要逐页阅读，请使用上一页或下一页'
        return false
      }
      if (!this.resourceReady) {
        this.actionError = '当前页尚未加载完成，请稍后再试'
        return false
      }
      if (this.remainingSeconds > 0) {
        this.actionError = `请在当前页再停留 ${this.remainingSeconds} 秒`
        return false
      }
      return this.turnTo(page)
    },
    async turnTo(targetPage) {
      if (this.actionLoading) return false
      if (!this.resourceReady) {
        this.actionError = '当前页尚未加载完成，请稍后再试'
        return false
      }
      this.actionLoading = true
      this.actionError = ''

      try {
        const progress = await turnTextbookPage(this.textbookId, {
          from_page: this.currentPage,
          target_page: targetPage,
          version: Number(this.progress.version),
          request_id: this.createRequestId('turn', targetPage)
        })
        this.applyProgress(progress)
        return true
      } catch (error) {
        this.actionError = error.message || '翻页失败'
        return false
      } finally {
        this.actionLoading = false
      }
    },
    async completeReading() {
      if (this.actionLoading || this.isCompleted) return
      if (!this.resourceReady) {
        this.actionError = '当前页尚未加载完成，请稍后再试'
        return
      }
      if (this.remainingSeconds > 0) {
        this.actionError = `请在当前页再停留 ${this.remainingSeconds} 秒`
        return
      }

      this.actionLoading = true
      this.actionError = ''
      try {
        const progress = await completeTextbookProgress(this.textbookId, {
          version: Number(this.progress.version),
          request_id: this.createRequestId('complete', this.currentPage)
        })
        this.applyProgress(progress)
        this.$emit('completed', progress)
      } catch (error) {
        this.actionError = error.message || '完成状态提交失败'
      } finally {
        this.actionLoading = false
      }
    },
    createRequestId(action, page) {
      return `${action}-${Number(this.textbookId)}-${Number(page)}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    }
  }
}
</script>

<style scoped>
.controlled-reader {
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
}

.controlled-reader__toolbar {
  min-height: 60px;
  padding: 8px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.controlled-reader__navigation,
.controlled-reader__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.reader-button {
  padding: 8px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  cursor: pointer;
}

.reader-button:hover:not(:disabled) {
  color: #1677ff;
  border-color: #1677ff;
}

.reader-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.reader-button.is-primary {
  color: #fff;
  border-color: #1677ff;
  background: #1677ff;
}

.controlled-reader__page {
  min-width: 88px;
  text-align: center;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
}

.controlled-reader__wait {
  color: #d97706;
}

.controlled-reader__ready,
.controlled-reader__completed {
  color: #15803d;
}

.controlled-reader__message {
  padding: 9px 20px;
  color: #b42318;
  background: #fef3f2;
  border-bottom: 1px solid #fecdca;
}

.controlled-reader__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.controlled-reader__state {
  display: flex;
  flex: 1;
  min-height: 320px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #6b7280;
}

.controlled-reader__state.is-error {
  color: #b42318;
}

.controlled-reader__spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #dbeafe;
  border-top-color: #1677ff;
  border-radius: 50%;
  animation: controlled-reader-spin 0.8s linear infinite;
}

@keyframes controlled-reader-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .controlled-reader__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
